/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, Form, FormGroup } from 'react-bootstrap'
import TextArea from '@mdsol/lego/lib/TextArea'
import Modal from '~/components/Modal'
import { useServiceContext } from '~/root'
import Utils from '~/lib/Utils'
import _ from 'lodash'
import { useNavigate, useParams } from 'react-router'
import {
  permanentDeleteFile,
  permanentDeleteMultipleFiles,
  getFileUsageById,
  permanentDeleteAllFilesInFolder,
  permanentDeleteAllFilesFromPropertiesSearch
} from '~/services/api/files'
import { File } from '~/constants/file'
import { useEsignature } from '~/hooks/useEsignature'

interface PermanentDeleteModalProps {
  fsi: any
  options?: any
  onClose?: () => void
}

const PermanentDelete: React.FC<PermanentDeleteModalProps> = ({ fsi, options = {}, onClose }) => {
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { $rootScope } = context
  const { translate } = context.service.filter
  const navigate = useNavigate()
  const params = useParams()
  const { signAction } = useEsignature()
  const [isOpen, setIsOpen] = useState(true)
  const [showUsageWarning, setShowUsageWarning] = useState(false)
  const [deleteComment, setDeleteComment] = useState('')
  const [fileId, setFileId] = useState<number | number[] | null>(null)
  const [file, setFile] = useState<any>({})

  useEffect(() => {
    getFile()
  }, [fsi, options])

  const doClose = (): void => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const getFile = async (): Promise<void> => {
    if (fsi?.id) {
      setFile(fsi)
      setFileId(_.isArray(options.selectedIds) && options.selectedIds.length > 1 ? options.selectedIds : fsi?.id)

      // Check if file is used in document types (for templates)
      if (fsi?.systemFolderType === File.systemFolderType.TEMPLATES) {
        try {
          const data = await getFileUsageById(fsi.id)
          if (data?.docTypeTplsWithTemplateFile && data.docTypeTplsWithTemplateFile.length > 0) {
            setShowUsageWarning(true)
          }
        } catch (error) {
          console.error('Failed to check file usage:', error)
        }
      }
    } else if (!isMultiselect() && !options?.selectedWholeResult) {
      doClose()
    }
  }

  const isMultiselect = (): boolean => {
    return options?.selectedIds && options?.selectedIds?.length > 1
  }

  const save = async (): Promise<void> => {
    doClose()

    let successMsg = translate('BROWSER_PERMANENT_DELETE_DOC_SUCCESS_MSG')
    let currentFileId = fileId

    if (isMultiselect()) {
      currentFileId = options?.selectedIds
    } else {
      if (file.type === File.type.FOLDER) {
        successMsg = translate('BROWSER_PERMANENT_DELETE_FOLDER_SUCCESS_MSG')
      }
    }

    try {
      // Get e-signature if required
      let esignature: any = {}
      if (auth.requireESignatureForFileCommands?.()) {
        esignature = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_DELETE')
      }

      // Perform deletion
      if (options && options.selectedWholeResult) {
        // Whole result selection - requires special API endpoints
        if (options.isPropertiesSearch && options.meta && options.filter && options.filter.metadataCriteria) {
          // Properties search - delete all matching files
          const filter = { ...options.filter, workflowIsNull: true }
          await permanentDeleteAllFilesFromPropertiesSearch({
            propertySearchRequestDTO: { filter },
            esignatureDTO: esignature,
            deletionComment: deleteComment
          })
        } else if (options.parentFolderId) {
          // Folder - delete all files in folder
          await permanentDeleteAllFilesInFolder(options.parentFolderId, {
            esignatureDTO: esignature,
            filter: options.filter,
            deletionComment: deleteComment
          })
        }
      } else {
        // Single file or multi-selection
        if (_.isArray(currentFileId)) {
          // Multiple files deletion
          await permanentDeleteMultipleFiles({
            ids: currentFileId,
            esignature,
            deletionComment: deleteComment
          })
        } else {
          // Single file deletion
          await permanentDeleteFile(currentFileId as number, {
            esignature,
            deletionComment: deleteComment
          })
        }
      }

      // Success - emit events and show toast
      window.dispatchEvent(new CustomEvent('updateFiles', { detail: 'PERMANENT_DELETE_FSI' }))
      window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
      window.dispatchEvent(new CustomEvent('updateDashboard'))
      window.dispatchEvent(new CustomEvent('updateSearchFiles'))
      window.dispatchEvent(new CustomEvent('fileupdate:permanentdelete'))

      toast.success(successMsg)

      // Navigate to parent folder if current folder was deleted
      if (params.folder && parseInt(params.folder as string) === currentFileId && file?.path) {
        const lastPath = file.path[file.path.length - 1]
        if (lastPath?.id) {
          setTimeout(() => {
            navigate(`/browser/${lastPath.id}`)
          }, 200)
        }
      }
    } catch (error: any) {
      // Handle errors (including e-signature cancellation)
      if (error?.message !== 'E-signature cancelled') {
        console.error('Permanent delete failed:', error)
        toast.error(error?.message || error?.data?.message || 'Permanent delete failed')
      }
    }
  }

  return (
    <Modal show={isOpen} handleClose={doClose} title={translate('BROWSER_PERMANENT_DELETE_ITEM_TITLE')} size="md">
      <Modal.Body>
        {(options?.selectedWholeResult || isMultiselect()) && (
          <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
            {translate('BROWSER_PERMANENT_DELETE_CONFIRM_QUESTION_MULTIPLE_DOCS')}
          </Form.Label>
        )}

        {!options?.selectedWholeResult && !isMultiselect() && file?.type && (
          <FormGroup className="wparag">
            <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
              {translate('BROWSER_PERMANENT_DELETE_CONFIRM_QUESTION_SINGLE_' + file?.type)}
            </Form.Label>
            <p className="whitespaceprewrap" style={{ fontSize: '12px' }}>
              {file?.name}
            </p>
          </FormGroup>
        )}

        {showUsageWarning && !isMultiselect() && (
          <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>{translate('FILE_IS_USED_IN_DOC_TYPE')}</Form.Label>
        )}

        <div className="form-group md-form-group">
          <FormGroup controlId="384631d8-8301-4e46-9987-79d86f1e81aa" className="w100p">
            <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
              {translate('DELETE_FOLDER_DOCUMENT_COMMENT')}
            </Form.Label>
            <TextArea
              {...({
                onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setDeleteComment(e.target.value?.length ? e.target.value : '')
                },
                maxLength: 255,
                id: '019f26c3-4fdf-4ab2-a7c5-f18f18fc5747',
                value: deleteComment,
                rows: 6
              } as any)}
            />
          </FormGroup>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button data-action={'ok'} variant="link" onClick={doClose} id="a69ef76f-db99-4314-bd0e-52c4cfc1338e">
          {translate('CANCEL')}
        </Button>
        <Button data-action={'ok'} className="btn btn-primary" onClick={save} id="87375811-f3d7-4d36-bce0-24e16bc6785e">
          {translate('OK')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PermanentDelete
